import { saveScheduleItem, deleteScheduleItem } from '../utils/scheduleService';
import { getFollowingRecurringDeleteIds } from '../components/Schedule/scheduleRecurringUtils';

export function useScheduleLessonActions({
  modalDate, setModalDate, setModalOpen, setAddLessonSaving,
  getStudentName, groups, students,
  lessons, setLessons,
  selectedLesson, setSelectedLesson, setEditModalOpen,
  setPendingLessonUpdate, setRecurringEditModalOpen, setUpdateLessonSaving,
  setDeleteLessonSaving,
  pendingLessonDelete, setPendingLessonDelete, setDeleteRecurringModalOpen, setDeleteRecurringSaving,
}) {
  const handleAddLesson = (date) => {
    setModalDate(date);
    setModalOpen(true);
  };

  const handleSaveLesson = ({ type, selectedId, title, from, to, repeatWeekly, allDay }, dateFromModal) => {
    // Используем date из пропсов модального окна в первую очередь
    const targetDate = dateFromModal || modalDate || new Date();
    if (!targetDate) {
      console.error('❌ No date available for saving lesson');
      return;
    }

    console.log('📅 Saving lesson for date:', targetDate);
    console.log('📅 dateFromModal:', dateFromModal);
    console.log('📅 modalDate:', modalDate);

    // Validate required fields based on type
    if (type === 'lesson' && !selectedId) return;
    if (type === 'event' && !title) return;

    // Parse selectedId to determine if it's a student or group
    let studentId = null;
    let groupId = null;
    let itemTitle = title;

    if (type === 'lesson' && selectedId) {
      if (selectedId.startsWith('student-')) {
        // Убираем 'student-' и все дефисы после него до первого символа
        studentId = selectedId.replace(/^student-+/, '').replace(/^-+/, ''); // Убираем 'student-' и возможные дефисы в начале slug
        console.log('🔍 Parsed studentId from selectedId:', studentId);
        // Get student name for display, but don't use it as lesson title
        const studentName = getStudentName(studentId, null);
        itemTitle = `${studentName}'s Lesson`;
      } else if (selectedId.startsWith('group-')) {
        groupId = selectedId.replace(/^group-+/, ''); // Убираем 'group-' и возможные лишние дефисы
        const groupName = (groups && groups[groupId]?.name) || 'Group';
        itemTitle = `${groupName} Lesson`;
      }
    }

    // Create date without timezone issues - use ISO format YYYY-MM-DD
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    console.log('📅 Creating date string:', dateString, 'from targetDate:', targetDate);

    const baseDate = new Date(year, targetDate.getMonth(), targetDate.getDate());
    let itemsToAdd = [];
    let recurringId = repeatWeekly ? `series_${Date.now()}` : undefined;
    if (repeatWeekly) {
      for (let i = 0; i < 12; i++) {
        const itemDate = new Date(baseDate);
        itemDate.setDate(baseDate.getDate() + i * 7);
        itemsToAdd.push({
          // Не создаем id здесь - Supabase сам сгенерирует UUID
          type: type,
          studentId: type === 'lesson' ? studentId : null,
          groupId: type === 'lesson' ? groupId : null,
          title: itemTitle,
          date: `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`, // ISO format YYYY-MM-DD
          from: allDay ? null : from,
          to: allDay ? null : to,
          allDay: allDay,
          status: type === 'lesson' ? 'in_progress' : 'in_progress',
          recurringId
        });
      }
      console.log('Создаём серию с recurringId:', recurringId, itemsToAdd);
    } else {
      itemsToAdd.push({
        // Не создаем id здесь - Supabase сам сгенерирует UUID
        type: type,
        studentId: type === 'lesson' ? studentId : null,
        groupId: type === 'lesson' ? groupId : null,
        title: itemTitle,
        date: dateString, // ISO format YYYY-MM-DD
        from: allDay ? null : from,
        to: allDay ? null : to,
        allDay: allDay,
        status: type === 'lesson' ? 'in_progress' : 'in_progress',
      });
    }

    console.log('➕ Adding new items:', itemsToAdd);

    // Сохраняем каждый элемент в Supabase
    const savePromises = itemsToAdd.map(async (item) => {
      try {
        const saved = await saveScheduleItem(item);
        console.log('✅ Saved to Supabase:', saved);
        return saved;
      } catch (error) {
        console.error('❌ Error saving to Supabase:', error);
        throw error;
      }
    });

    setAddLessonSaving(true);
    Promise.all(savePromises)
      .then((savedItems) => {
        setLessons(prev => {
          const newLessons = [...prev, ...savedItems];
          console.log('📝 Updated lessons state:', newLessons);
          return newLessons;
        });
        setModalOpen(false);
      })
      .catch((error) => {
        console.error('Error adding lesson to schedule:', error);
      })
      .finally(() => {
        setAddLessonSaving(false);
      });
  };

  const handleEditLesson = (lesson) => {
    setSelectedLesson(lesson);
    setEditModalOpen(true);
  };

  const handleUpdateLesson = (updatedLesson) => {
    // Check if this is a recurring lesson (has repeatWeekly flag or is part of a series)
    const isRecurring = updatedLesson.repeatWeekly ||
                       lessons.some(l => l.id !== updatedLesson.id &&
                                       l.type === updatedLesson.type &&
                                       ((l.studentId === updatedLesson.studentId && updatedLesson.studentId) ||
                                        (l.groupId === updatedLesson.groupId && updatedLesson.groupId)) &&
                                       l.from === updatedLesson.from &&
                                       l.to === updatedLesson.to);

    if (isRecurring) {
      setPendingLessonUpdate(updatedLesson);
      setRecurringEditModalOpen(true);
      setEditModalOpen(false);
    } else {
      setUpdateLessonSaving(true);
      updateLesson(updatedLesson, 'single');
    }
  };

  const updateLesson = (updatedLesson, editType) => {
    console.log('updateLesson called with:', updatedLesson, 'editType:', editType);
    // eslint-disable-next-line no-unused-vars
    let lessonsToUpdate = [];

    setLessons(prev => {
      let newLessons;

      if (editType === 'single') {
        // Update only this specific lesson
        newLessons = prev.map(l => {
          if (l.id === selectedLesson.id) {
            // Если изменился studentId или groupId, обновляем title с новым именем студента/группы
            let finalUpdatedLesson = { ...updatedLesson, id: l.id };
            // Очищаем studentId если выбран groupId, и наоборот
            if (finalUpdatedLesson.groupId) {
              finalUpdatedLesson.studentId = null;
            } else if (finalUpdatedLesson.studentId) {
              finalUpdatedLesson.groupId = null;
            }
            if (updatedLesson.type === 'lesson') {
              if (updatedLesson.studentId && updatedLesson.studentId !== selectedLesson.studentId) {
                const studentName = (students && students[updatedLesson.studentId]?.name) || 'Student';
                finalUpdatedLesson.title = `${studentName}'s Lesson`;
              } else if (updatedLesson.groupId && updatedLesson.groupId !== selectedLesson.groupId) {
                const groupName = (groups && groups[updatedLesson.groupId]?.name) || 'Group';
                finalUpdatedLesson.title = `${groupName} Lesson`;
              }
            }
            return finalUpdatedLesson;
          }
          return l;
        });
      } else if (editType === 'following') {
        // Массовое обновление: recurringId + дата (с учётом сдвига дат)
        if (updatedLesson.recurringId && selectedLesson) {
          const oldDate = new Date(selectedLesson.date);
          const newDate = new Date(updatedLesson.date);
          const dayDelta = Math.round((newDate - oldDate) / (1000 * 60 * 60 * 24));
          newLessons = prev.map(l => {
            if (l.recurringId === updatedLesson.recurringId) {
              const lDate = new Date(l.date);
              const isSameOrAfter = lDate.getFullYear() > oldDate.getFullYear() ||
                (lDate.getFullYear() === oldDate.getFullYear() && lDate.getMonth() > oldDate.getMonth()) ||
                (lDate.getFullYear() === oldDate.getFullYear() && lDate.getMonth() === oldDate.getMonth() && lDate.getDate() >= oldDate.getDate());
              if (isSameOrAfter) {
                // Сдвигаем дату на тот же дельта-дней
                const shiftedDate = new Date(lDate);
                shiftedDate.setDate(lDate.getDate() + dayDelta);
                // Если изменился studentId, обновляем title с новым именем студента
                let finalUpdatedLesson = { ...updatedLesson, id: l.id, date: shiftedDate.toDateString(), recurringId: l.recurringId };
                if (updatedLesson.type === 'lesson' && updatedLesson.studentId && updatedLesson.studentId !== selectedLesson.studentId) {
                  finalUpdatedLesson.title = 'English Lesson';
                }
                return finalUpdatedLesson;
              }
            }
            return l;
          });
        } else {
          // fallback: старая логика для старых серий
          const lessonDate = new Date(selectedLesson.date);
          const newDate = new Date(updatedLesson.date);
          const dayDelta = Math.round((newDate - lessonDate) / (1000 * 60 * 60 * 24));
          newLessons = prev.map(l => {
            const lDate = new Date(l.date);
            const isSameSeries =
              l.type === updatedLesson.type &&
              l.studentId === updatedLesson.studentId &&
              l.from === updatedLesson.from &&
              l.to === updatedLesson.to &&
              (l.title === updatedLesson.title || !l.title || !updatedLesson.title);
            const isSameOrAfter = lDate.getFullYear() > lessonDate.getFullYear() ||
              (lDate.getFullYear() === lessonDate.getFullYear() && lDate.getMonth() > lessonDate.getMonth()) ||
              (lDate.getFullYear() === lessonDate.getFullYear() && lDate.getMonth() === lessonDate.getMonth() && lDate.getDate() >= lessonDate.getDate());
            if ((l.id === selectedLesson.id || (isSameSeries && isSameOrAfter))) {
              const shiftedDate = new Date(lDate);
              shiftedDate.setDate(lDate.getDate() + dayDelta);
              // Если изменился studentId, обновляем title с новым именем студента
              let finalUpdatedLesson = { ...updatedLesson, id: l.id, date: shiftedDate.toDateString() };
              if (updatedLesson.type === 'lesson' && updatedLesson.studentId && updatedLesson.studentId !== selectedLesson.studentId) {
                finalUpdatedLesson.title = 'English Lesson';
              }
              return finalUpdatedLesson;
            }
            return l;
          });
        }
      }

      // Сохраняем обновленные элементы в Supabase
      const updatedItems = newLessons.filter(l =>
        prev.some(p => p.id === l.id && JSON.stringify(p) !== JSON.stringify(l))
      );

      const savePromises = updatedItems.map((item) => {
        // Capture prev status from the snapshot before saveScheduleItem
        // resolves — needed so the StudentProfile listener can decide
        // add-vs-remove without depending on its own ref timing.
        const prevItem = prev.find(p => p.id === item.id);
        const prevStatus = prevItem?.status;
        return saveScheduleItem(item).then((saved) => {
          if (saved && saved.id !== item.id) {
            setLessons(prevLessons => prevLessons.map(l =>
              l.id === item.id ? saved : l
            ));
          }
          // StudentProfile listens for lessonStatusUpdated to drive its
          // auto-charge logic. saveScheduleItem only emits scheduleUpdated,
          // so the profile would otherwise miss status changes made here.
          // The listener short-circuits when prevStatus === newStatus, so
          // dispatching unconditionally is safe (no charge churn on
          // non-status edits).
          const finalId = saved?.id || item.id;
          const finalStatus = saved?.status || item.status;
          if (finalId && finalStatus) {
            window.dispatchEvent(new CustomEvent('lessonStatusUpdated', {
              detail: { lessonId: finalId, status: finalStatus, prevStatus }
            }));
          }
          return saved;
        }).catch((error) => {
          console.error('❌ Error updating in Supabase:', error);
          throw error;
        });
      });
      Promise.all(savePromises).then(() => {
        setEditModalOpen(false);
        setSelectedLesson(null);
        setRecurringEditModalOpen(false);
        setPendingLessonUpdate(null);
      }).finally(() => {
        setUpdateLessonSaving(false);
      });

      // Сохранение в localStorage убрано - все сохраняется только в Supabase

      console.log('setLessons newLessons:', newLessons);
      return newLessons;
    });

  };

  const handleDeleteLesson = async (lessonToDelete) => {
    setDeleteLessonSaving(true);
    try {
      await deleteScheduleItem(lessonToDelete.id);
      console.log('✅ Deleted from Supabase:', lessonToDelete.id);
      setLessons(prev => prev.filter(lesson => lesson.id !== lessonToDelete.id));
      setEditModalOpen(false);
      setSelectedLesson(null);
    } catch (error) {
      console.error('❌ Error deleting from Supabase:', error);
    } finally {
      setDeleteLessonSaving(false);
    }
  };

  const handleDeleteClick = (lesson) => {
    // Проверяем, повторяющийся ли урок (recurringId или серия)
    const isRecurring = lesson.recurringId || lessons.some(l => l.id !== lesson.id && l.type === lesson.type && l.studentId === lesson.studentId && l.from === lesson.from && l.to === lesson.to);
    if (isRecurring) {
      setPendingLessonDelete(lesson);
      setDeleteRecurringModalOpen(true);
      setEditModalOpen(false);
    } else {
      handleDeleteLesson(lesson);
    }
  };

  const handleDeleteRecurringThis = async () => {
    if (!pendingLessonDelete) return;
    setDeleteRecurringSaving(true);
    try {
      await handleDeleteLesson(pendingLessonDelete);
      setDeleteRecurringModalOpen(false);
      setPendingLessonDelete(null);
    } finally {
      setDeleteRecurringSaving(false);
    }
  };

  const handleDeleteRecurringFollowing = async () => {
    if (!pendingLessonDelete) return;
    const ids = getFollowingRecurringDeleteIds(pendingLessonDelete, lessons);
    setDeleteRecurringSaving(true);
    try {
      if (ids.length > 0) {
        await Promise.all(ids.map((id) => deleteScheduleItem(id)));
      }
      const idSet = new Set(ids);
      setLessons((prev) => prev.filter((l) => !idSet.has(l.id)));
      setEditModalOpen(false);
      setSelectedLesson(null);
      setDeleteRecurringModalOpen(false);
      setPendingLessonDelete(null);
    } catch (e) {
      console.error('❌ Error deleting recurring series:', e);
    } finally {
      setDeleteRecurringSaving(false);
    }
  };

  return {
    handleAddLesson,
    handleSaveLesson,
    handleEditLesson,
    handleUpdateLesson,
    updateLesson,
    handleDeleteLesson,
    handleDeleteClick,
    handleDeleteRecurringThis,
    handleDeleteRecurringFollowing,
  };
}
